import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  ArrowRight, Phone, MessageCircle, MapPin, 
  Navigation, Star, DollarSign, X, Send
} from 'lucide-react';
import { useRide } from '@/context/RideContext';
import { rideService } from '@/services/rideService';
import { chatService } from '@/services/chatService';
import type { Ride, Driver } from '@/types';

const PassengerRide: React.FC = () => {
  const navigate = useNavigate();
  const { rideId } = useParams();
  const { currentRide, messages, sendMessage, cancelRide, driverArrived } = useRide();
  
  const [ride, setRide] = useState<Ride | null>(null);
  const [driver, setDriver] = useState<Driver | null>(null);
  const [showChat, setShowChat] = useState(false);
  const [messageText, setMessageText] = useState('');
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelReason, setCancelReason] = useState('');

  useEffect(() => {
    if (rideId) {
      loadRide();
    }
  }, [rideId]);

  const loadRide = async () => {
    if (!rideId) return;
    try {
      const rideData = await rideService.getRide(rideId);
      if (rideData) {
        setRide(rideData);
        // In production, load driver details
      }
    } catch (error) {
      console.error('Error loading ride:', error);
    }
  };

  const handleSendMessage = async () => {
    if (!messageText.trim()) return;
    await sendMessage(messageText);
    setMessageText('');
  };

  const handleCancel = async () => {
    if (!cancelReason.trim()) return;
    await cancelRide(cancelReason);
    navigate('/passenger/rides');
  };

  const getStatusText = (status: string) => {
    const texts: Record<string, string> = {
      searching: 'جاري البحث عن سائق...',
      driver_assigned: 'تم تعيين سائق',
      driver_arrived: 'السائق في الموقع',
      in_progress: 'الرحلة جارية',
      completed: 'تم إكمال الرحلة',
      cancelled: 'تم إلغاء الرحلة',
    };
    return texts[status] || status;
  };

  const activeRide = currentRide || ride;

  if (!activeRide) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-gray-500">الرحلة غير موجودة</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-40">
        <div className="flex items-center gap-4 p-4">
          <button
            onClick={() => navigate('/passenger/home')}
            className="p-2 hover:bg-gray-100 rounded-xl transition-colors"
          >
            <ArrowRight className="w-6 h-6" />
          </button>
          <div className="flex-1">
            <h1 className="text-xl font-bold">تفاصيل الرحلة</h1>
            <p className="text-sm text-gray-500">#{activeRide.id.slice(-8).toUpperCase()}</p>
          </div>
        </div>
      </header>

      {/* Status Banner */}
      <div className="bg-primary-500 text-white p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-white/70 text-sm">حالة الرحلة</p>
            <p className="font-bold text-lg">{getStatusText(activeRide.status)}</p>
          </div>
          <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
            <Navigation className="w-6 h-6 animate-pulse" />
          </div>
        </div>
      </div>

      {/* Map Placeholder */}
      <div className="h-48 bg-gray-200 flex items-center justify-center">
        <p className="text-gray-500">خريطة المسار</p>
      </div>

      {/* Driver Info */}
      {activeRide.driverId && (
        <div className="bg-white p-4 shadow-sm">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center">
              <img 
                src={driver?.photoURL || '/default-driver.png'} 
                alt="Driver" 
                className="w-16 h-16 rounded-full object-cover"
              />
            </div>
            <div className="flex-1">
              <p className="font-bold">{driver?.name || 'السائق'}</p>
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                <span>{driver?.rating || '5.0'}</span>
                <span>•</span>
                <span>{driver?.vehicle?.make} {driver?.vehicle?.model}</span>
              </div>
              <p className="text-sm text-gray-500" dir="ltr">
                {driver?.vehicle?.plateNumber}
              </p>
            </div>
            <div className="flex gap-2">
              <button 
                onClick={() => setShowChat(true)}
                className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center"
              >
                <MessageCircle className="w-5 h-5 text-primary-600" />
              </button>
              <button className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                <Phone className="w-5 h-5 text-green-600" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Ride Details */}
      <div className="flex-1 p-4">
        <div className="bg-white rounded-2xl p-4 shadow-sm">
          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                <MapPin className="w-4 h-4 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">نقطة الانطلاق</p>
                <p className="font-medium">{activeRide.pickup?.address}</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                <MapPin className="w-4 h-4 text-red-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">الوجهة</p>
                <p className="font-medium">{activeRide.destination?.address}</p>
              </div>
            </div>
          </div>

          <div className="border-t mt-4 pt-4">
            <div className="flex justify-between items-center">
              <span className="text-gray-500">طريقة الدفع</span>
              <span className="font-medium">{activeRide.paymentMethod}</span>
            </div>
            <div className="flex justify-between items-center mt-2">
              <span className="text-gray-500">السعر المقدر</span>
              <span className="font-bold text-lg">{activeRide.estimatedPrice} ج.س</span>
            </div>
          </div>
        </div>
      </div>

      {/* Cancel Button */}
      {['searching', 'driver_assigned'].includes(activeRide.status) && (
        <div className="p-4">
          <button
            onClick={() => setShowCancelModal(true)}
            className="w-full bg-red-500 text-white py-4 rounded-xl font-bold hover:bg-red-600 transition-colors"
          >
            إلغاء الرحلة
          </button>
        </div>
      )}

      {/* Chat Modal */}
      {showChat && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-end">
          <div className="bg-white rounded-t-3xl w-full h-[80vh] flex flex-col">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="font-bold">الدردشة مع السائق</h3>
              <button onClick={() => setShowChat(false)}>
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <div className="flex-1 overflow-auto p-4">
              {messages.length === 0 ? (
                <p className="text-center text-gray-500">لا توجد رسائل</p>
              ) : (
                messages.map((msg) => (
                  <div 
                    key={msg.id} 
                    className={`mb-4 ${msg.senderType === 'passenger' ? 'text-left' : 'text-right'}`}
                  >
                    <div className={`inline-block px-4 py-2 rounded-2xl ${
                      msg.senderType === 'passenger' 
                        ? 'bg-primary-500 text-white' 
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {msg.content}
                    </div>
                  </div>
                ))
              )}
            </div>

            <div className="p-4 border-t">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={messageText}
                  onChange={(e) => setMessageText(e.target.value)}
                  placeholder="اكتب رسالتك..."
                  className="flex-1 border border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:border-primary-500"
                  onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                />
                <button
                  onClick={handleSendMessage}
                  className="w-12 h-12 bg-primary-500 text-white rounded-xl flex items-center justify-center"
                >
                  <Send className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Cancel Modal */}
      {showCancelModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-md p-6">
            <h3 className="text-xl font-bold mb-4">إلغاء الرحلة</h3>
            <p className="text-gray-500 mb-4">يرجى إخبارنا بسبب الإلغاء</p>
            
            <textarea
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              placeholder="سبب الإلغاء..."
              className="w-full border border-gray-200 rounded-xl p-4 h-32 focus:outline-none focus:border-primary-500 resize-none mb-4"
            />

            <div className="flex gap-3">
              <button
                onClick={() => setShowCancelModal(false)}
                className="flex-1 py-3 border border-gray-200 rounded-xl font-medium"
              >
                تراجع
              </button>
              <button
                onClick={handleCancel}
                disabled={!cancelReason.trim()}
                className="flex-1 py-3 bg-red-500 text-white rounded-xl font-medium disabled:opacity-50"
              >
                تأكيد الإلغاء
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PassengerRide;
